import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import {
  User as UserIcon,
  Mail,
  Phone,
  Check,
  Camera,
  UserCircle,
} from 'lucide-react';
import { IMAGE_API_BASE_URL } from '../../config/api';

interface AccountSummarySectionProps {
  user: any;
  uploadingPhoto: boolean;
  setIsFacialVerificationOpen: (isOpen: boolean) => void;
}

const AccountSummarySection = ({
  user,
  uploadingPhoto,
  setIsFacialVerificationOpen,
}: AccountSummarySectionProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <UserCircle className="h-5 w-5 text-primary" />
          Account Summary
        </CardTitle>
        <CardDescription>Your account overview</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col items-center space-y-4 mb-4">
          <div
            className={`h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-primary/10 flex items-center justify-center relative overflow-hidden border-2 border-primary/20 transition-all shadow-sm ${
              user?.role !== 'super_admin'
                ? 'group cursor-pointer hover:border-primary'
                : ''
            }`}
            onClick={
              user?.role !== 'super_admin'
                ? () => setIsFacialVerificationOpen(true)
                : undefined
            }
          >
            {user?.verification?.facialVerification ? (
              <img
                src={`${IMAGE_API_BASE_URL}${user.verification.facialVerification}`}
                alt="Avatar"
                className="w-full h-full object-cover"
                key={user.verification.facialVerification}
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
              />
            ) : (
              <UserIcon className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
            )}

            {user?.role !== 'super_admin' && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
              </div>
            )}

            {uploadingPhoto && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>

          {user?.role !== 'super_admin' && (
            <div className="text-center">
              <Button
                variant="outline"
                size="sm"
                className="h-7 sm:h-8 text-xs gap-2"
                onClick={() => setIsFacialVerificationOpen(true)}
                disabled={uploadingPhoto}
              >
                <Camera className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                Change Photo
              </Button>
            </div>
          )}

          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <p className="font-semibold text-base sm:text-lg">
                {user?.fullName}
              </p>
              {(user?.emailVerified || user?.phoneVerified) && (
                <Check className="h-5 w-5 text-green-500 fill-green-500/10" />
              )}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground capitalize font-medium">
              {user?.roleData?.description || user?.role?.replace('_', ' ')}{' '}
              Account
            </p>
          </div>
        </div>

        <div className="space-y-2 pb-4">
          <div className="flex items-center space-x-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{user?.email}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">+{user?.phone || 'Not provided'}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AccountSummarySection;
